<template>
  <div id="dropzone-root" class="gk-vue-dropzone" :class="['type-' + image_field_name]">
    <div v-show="!hideZone && !file && !fileUrl" id="dropzone" class="dropzone dz-clickable" :class="file ? 'dropzone-border-none dz-started dz-max-files-reached' : 'dropzone-border'">
      
      <div v-if="image_field_name === 'avatar'" class="dropzone-hint">
        <div class="no-avatar">
          {{ avatar_initials }}
        </div>
        <div class="no-avatar-hint">
          <font-awesome-icon icon="fa-solid fa-upload" class="" />
          {{ $gettext('Upload avatar') }}
        </div>
      </div>

      <div v-else class="dropzone-hint">
        <font-awesome-icon icon="fa-solid fa-image-slash" class="dropzone-hint-icon" />
      </div>
    </div>
    <div id="dropzonePreview" v-show="!hidePreview && preloaded_file" class="dropzone dz-default dz-file-preview dropzone-border-none dropzone-preview dropzone-preview-image" :class="{ uploading: uploading }">
      <LoadingSpinner v-if="uploading" />
      <ImageViewer :zoomable="true" :file-path="preloaded_file" ref="previewImage" />
    </div>
    <div v-if="!hideActions" class="dropzone-file-actions" :class="{ visible: showFileActions }">
      <GekkoButton 
        v-show="!onlyChange && (file || fileUrl)"
        @click="removeImage"
        :text="$gettext('Remove')" 
        icon="fa-solid fa-trash-can"
        class="text-danger"
        variant="link"
        size="sm"
      />
      <GekkoButton 
        v-show="onlyChange && (file || fileUrl) && !disableEdit"
        id="click-change-image"
        :text="$gettext('Replace')" 
        icon="fa-solid fa-upload"
        class="text-info"
        variant="link"
        size="sm"
      />
  </div>
  </div>
</template>

<script>
import customAxios from '../../store/services/axiosConfig'
import ImageViewer from '@/components/ui/ImageViewer'
import GekkoButton from "@/components/GekkoButton";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
  name: 'ImageDropzone',
  components: { 
    ImageViewer,
    GekkoButton,
    LoadingSpinner
  },
  props: {
    preloaded_file: {
      type: String,
      default: null,
    },
    image_field_name: {
      type: String,
      default: null,
    },
    hidePreview: {
      type: Boolean,
      default: false,
    },
    hideZone: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => {},
    },
    disableEdit: {
      type: Boolean,
      default: false
    },
    disableRemove: {
      type: Boolean,
      default: false
    },
    onlyChange: {
      type: Boolean,
      default: false
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    avatar_initials: {
      type: String,
      default: null,
    }
  },
  data () {
    return {
      file: null,
      fileUrl: null,
      imageDropzone: null,
      initialized: false,
      uploading: false,
      defaultOptions: {
        method: 'patch',
        thumbnailWidth: null,
        acceptedFiles: 'image/*',
        thumbnailHeight: null,
        thumbnailMethod: 'contain',
        autoProcessQueue: false,
        maxFiles: 1,
        url: 'www.com',
        clickable: ['#dropzone'],
        headers: {
          // 'X-CSRFToken': document.head.querySelector('[name~=csrftoken][content]').content
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      }
    }
  },
  computed: {
    dropzoneOptions () {
      const options = {
        ...this.defaultOptions,
        ...this.options
      }
      // Merge `clickable` arrays if both exist
      if (Array.isArray(this.defaultOptions.clickable) && Array.isArray(this.options.clickable)) {
        options.clickable = [...this.defaultOptions.clickable, ...this.options.clickable]
      }
      return options
    },
    showFileActions () {
      return (
        (!this.onlyChange && (this.file || this.fileUrl)) || 
        (this.onlyChange && (this.file || this.fileUrl) && !this.disableEdit) || 
        (this.fileUrl)
      )
    }
  },
  watch: {
    preloaded_file (newVal, oldVal) {
      this.initDropzone()
      if (oldVal !== newVal) {
        this.displayExistingFile(newVal)
      }
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
    this.initDropzone()
  },
  beforeDestroy () {
    this.imageDropzone.destroy()
  },
  methods: {
    setupData () {
      // console.log(this.dropzoneOptions)
    },
    initDropzone () {
      if (!this.imageDropzone && !this.initialized) {
        this.initialized = true
        this.initialize()
      }
      if (this.preloaded_file) {
        this.displayExistingFile(this.preloaded_file)
      }
    },
    displayExistingFile (file) {
      if (!this.hidePreview) {
        this.fileUrl = file
        const mockFile = { name: 'filename', size: 12345, mock: true }
        const callback = null
        const crossOrigin = null
        const resizeThumbnail = false
        this.imageDropzone.displayExistingFile(mockFile, this.fileUrl, callback, crossOrigin, resizeThumbnail);
      }
    },
    downloadFile (fileUrl) {
      window.open(fileUrl)
    },
    initialize () {
      window.Dropzone.autoDiscover = false
      this.imageDropzone = new window.Dropzone(this.$el, this.dropzoneOptions)
      this.attachDropzoneEvents()
    },
    attachDropzoneEvents () {
      this.imageDropzone.on('success', (file, response) => {
        console.log('ImageDropzone::success')
        this.uploading = false
        this.$emit('uploaded', response)
      })
      this.imageDropzone.on('maxfilesexceeded', (file) => {
        this.removeAllFiles()
        this.addFile(file)
      })
      this.imageDropzone.on('addedfile', (file, response) => {
        if (file.mock || !file.type.includes('image/')) {
          return false
        }
        if (!file.mock) {
          if (this.file) {
            this.removeImage()
          }
          this.file = file
          console.log('ImageDropzone::addedfile()', this.file)
          this.$emit('change', this.file)
        }
      })
      if (!this.hidePreview) {
        this.imageDropzone.on('thumbnail', (file, dataURL) => {
          this.$refs.previewImage.updateFilePath(dataURL)
        })
      }
      this.imageDropzone.on('sending', (file, xhr, formData) => {
        console.log('ImageDropzone::sending')
        // TODO add loadingSpinner
        this.uploading = true
        this.$emit('sending', false)
        formData.append(this.image_field_name, file);
      })
    },
    removeImage () {
      this.file = null
      this.fileUrl = null
      this.imageDropzone.removeAllFiles()

      this.$emit('removed', false)

      if (this.dropzoneOptions.autoProcessQueue) {
        if (this.dropzoneOptions.delete_url) {
          customAxios.delete(this.dropzoneOptions.delete_url)
        } else if (this.dropzoneOptions.url !== 'www.com' && this.dropzoneOptions.method === 'patch') {
          var data = {}
          data[this.image_field_name] = null
          customAxios.patch(this.dropzoneOptions.url, data)
        }
      }
    }
  }
}
</script>

<style lang="scss">
  .gk-vue-dropzone {
    &.type-avatar {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .dropzone {
        background: none !important;
        .dropzone-hint {
          gap: 1rem !important;
          justify-content: flex-start !important;
          padding: 0 !important;
          .no-avatar {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #e7e7e7;
            width: 150px !important;
            height: 150px !important;
            border-radius: 50em !important;
            overflow: hidden;
            font-size: 32px;
            color: white;
            cursor: pointer;
          }
          .no-avatar-hint {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 14px;
            color: $colorBlue;
            cursor: pointer;
          }
        }
      }
      &#dropzone-root {
        .dropzone-border-none {
          margin-left: auto !important;
          margin: 0 auto !important;
        }
      }
      .dropzone-preview-image {
        width:150px !important;
        height: 150px !important;
        border-radius: 50em !important;
        overflow: hidden;

        &.uploading { display: flex !important; }
        .image-viewer {
          img { object-fit: cover !important; }
        }
      }
    }
  }
</style>
<style lang="scss" scoped>
  .dropzone-file-actions.visible {
    display: flex;
    margin-top: 10px;
    height: 30px;
  }
</style>